import React, { ChangeEvent, FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { Tour as TourContract } from '@luxuryescapes/contract-svc-tour';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import ImageUploadField from '~/components/Common/Forms/ImageUploadField';
import Spinner from '~/components/Common/Spinner';
import TourOfferTourStyleInput from '~/components/Tours/ToursListing/TourOfferTourStyleInput';

import useAppNavigation from '~/hooks/useAppNavigation';

import { formatDateDateTimeWidget, newDate } from '~/services/TimeService';
import { getTours } from '~/services/ToursService';

import { noop } from '~/utils/noop';
import { getSlug } from '~/utils/slugUtils';

import { getTourResultVisitedCountries } from '../../../ToursListing/Tables/tourSearchResultValueGetters';
import TourOfferCountryInput from '../../../ToursListing/TourOfferCountryInput';
import TourOfferTTCBrandInput from '../../../ToursListing/TourOfferTTCBrandInput';
import { TourDealPage, TourDealPageBody } from '../types';

type Props = {
  dealPage?: TourDealPage;
  isFetching?: boolean;
  children?: ReactNode;
  onSubmit: (formValues: TourDealPageBody) => void;
};

type TourOption = { id: string; name: string };

function getInitialFormValues(): TourDealPage {
  return {
    id: '',
    name: '',
    heading: '',
    subheading: '',
    slug: '',
    colourHex: '',
    dealType: 'FLASH_TOURS' as TourContract.TourDealType,
    startAt: '',
    endAt: '',
    heroImageKey: '',
    tours: [],
  } as unknown as TourDealPage;
}

export default function TourDealPageForm({ dealPage, isFetching, children, onSubmit = noop }: Props) {
  const { push } = useAppNavigation();

  const [formValues, setFormValues] = useState<TourDealPage>(getInitialFormValues());
  const [selectedTours, setSelectedTours] = useState<Array<TourOption>>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState<Array<TourContract.Tour>>([]);
  const [isTourLoading, setIsTourLoading] = useState(false);
  const [tourName, setTourName] = useState('');
  const [vendors, setVendors] = useState<Array<string>>([]);
  const [countries, setCountries] = useState<Array<string>>([]);
  const [tourStyles, setTourStyles] = useState<Array<string>>([]);

  const normalizeId = useCallback((id: string) => id.replace(/^tour-/, ''), []);

  useEffect(() => {
    if (dealPage) {
      setFormValues(dealPage);
      setSelectedTours(
        dealPage.tours.map((t) => {
          const id = normalizeId(t.id);
          return {
            id,
            name: t.name,
          };
        }),
      );
    }
  }, [dealPage, normalizeId]);

  const isSlugValid = useMemo(() => {
    return /^[a-zA-Z0-9-]+$/.test(formValues.slug || '');
  }, [formValues.slug]);

  const isColourHexValid = useMemo(() => {
    if (!formValues.colourHex) {
      return true;
    }
    return /^#[0-9A-Fa-f]{6}$/.test(formValues.colourHex);
  }, [formValues.colourHex]);

  const isFormValid = useMemo(() => {
    return (
      !!formValues.name &&
      !!formValues.heading &&
      !!formValues.slug &&
      isSlugValid &&
      !!formValues.dealType &&
      !!formValues.startAt &&
      !!formValues.endAt &&
      !!formValues.heroImageKey &&
      selectedTours.length > 0 &&
      isColourHexValid
    );
  }, [formValues, selectedTours, isSlugValid, isColourHexValid]);

  const handleChangeField = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event?.target?.name) {
        setFormValues({
          ...formValues,
          [event.target.name]: event.target.value ?? undefined,
        });
      }
    },
    [formValues],
  );

  const handleNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormValues((prev) => ({ ...prev, name: value, slug: getSlug(value) }));
  }, []);

  const handleAddImage = useCallback((_: string, imageId: string) => {
    setFormValues((prev) => ({ ...prev, heroImageKey: imageId }));
    return Promise.resolve();
  }, []);

  const handleCancel = useCallback(() => {
    push(`/tours/deals`);
  }, [push]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!/^[a-zA-Z0-9-]+$/.test(formValues.slug || '')) {
        return;
      }
      if (formValues.colourHex && !/^#[0-9A-Fa-f]{6}$/.test(formValues.colourHex)) {
        return;
      }
      const payload: TourDealPageBody = {
        name: formValues.name,
        heading: formValues.heading,
        subheading: formValues.subheading,
        slug: formValues.slug,
        colourHex: formValues.colourHex || undefined,
        dealType: formValues.dealType,
        startAt: formValues.startAt,
        endAt: formValues.endAt,
        heroImageKey: formValues.heroImageKey,
        tours: selectedTours.map((t) => t.id),
      };
      onSubmit(payload);
    },
    [formValues, onSubmit, selectedTours],
  );

  const dealStatus = useMemo(() => {
    const now = new Date();
    const start = formValues.startAt ? new Date(formValues.startAt) : null;
    const end = formValues.endAt ? new Date(formValues.endAt) : null;
    if (!start || !end) {
      return undefined;
    }
    if (now < start) {
      return { status: 'scheduled', color: 'green' };
    }
    if (now > end) {
      return { status: 'ended', color: 'red' };
    }
    return { status: 'currently live', color: 'green' };
  }, [formValues.endAt, formValues.startAt]);

  function getFilteredRows(allRows: Array<TourContract.Tour>, styles: Array<string>) {
    if (styles.length === 0) {
      return allRows;
    }
    return allRows.filter((r: TourContract.Tour) => styles.includes(r.tourStyle || ''));
  }

  // No global selection logic required; selection is page-scoped only

  const fetchTours = useCallback(async () => {
    setIsTourLoading(true);
    try {
      const res = await getTours({
        page: String(pageIndex + 1),
        per_page: String(20),
        searchString: tourName,
        vendors,
        countriesVisited: countries,
      } as Record<string, unknown>);
      const tours: Array<TourContract.Tour> = res?.result?.tours ?? [];
      setRows(tours);
      setTotal(res?.result?.total || 0);
    } catch (e) {
      setRows([]);
      setTotal(0);
    } finally {
      setIsTourLoading(false);
    }
  }, [pageIndex, countries, tourName, vendors]);

  useEffect(() => {
    const handle = setTimeout(() => {
      fetchTours();
    }, 300);
    return () => clearTimeout(handle);
  }, [fetchTours]);

  const columns: Array<GridColDef<TourContract.Tour>> = [
    {
      field: 'name',
      headerName: 'Tour Name',
      minWidth: 220,
      flex: 1,
      display: 'flex',
    },
    {
      field: 'brand',
      headerName: 'Operator',
      minWidth: 140,
      valueGetter: (_v, row) => row.brandObject?.name,
      display: 'flex',
    },
    {
      field: 'countriesVisited',
      headerName: 'Countries Visited',
      minWidth: 160,
      valueGetter: (_v, row) => getTourResultVisitedCountries(row),
      display: 'flex',
    },
    {
      field: 'tourStyle',
      headerName: 'Tour Style',
      minWidth: 140,
      valueGetter: (_v, row) => row.tourStyle || '',
      display: 'flex',
    },
    { field: 'id', headerName: 'ID', minWidth: 160, display: 'flex' },
  ];

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid size={4}>
          <TextField
            required
            fullWidth
            name="name"
            label="Deal Page Name"
            inputProps={{ maxLength: 100 }}
            helperText="The maximum length is 100 characters."
            onChange={handleNameChange}
            value={formValues.name}
          />
        </Grid>

        <Grid size={4}>
          <TextField
            required
            fullWidth
            name="slug"
            label="Deal Page Slug"
            onChange={handleChangeField}
            value={formValues.slug}
            error={!!formValues.slug && !isSlugValid}
            helperText={
              !!formValues.slug && !isSlugValid
                ? 'Only letters, numbers, and hyphens (a-z, A-Z, 0-9, -) are allowed.'
                : undefined
            }
          />
        </Grid>

        <Grid size={2}>
          <TextField
            required
            select
            fullWidth
            name="dealType"
            label="Deal Type"
            onChange={handleChangeField}
            value={formValues.dealType || ''}
          >
            {(['REGION', 'LUX_PLUS', 'FLASH_TOURS', 'LIMITED_TIME_SALE'] as Array<TourContract.TourDealType>).map(
              (type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ),
            )}
          </TextField>
        </Grid>

        <Grid size={2}>
          <TextField
            fullWidth
            name="colourHex"
            label="Colour Hex Code"
            placeholder="#FFFFFF"
            inputProps={{ maxLength: 7 }}
            value={formValues.colourHex || ''}
            onChange={handleChangeField}
            error={!!formValues.colourHex && !isColourHexValid}
            helperText={
              !!formValues.colourHex && !isColourHexValid
                ? 'Must be exactly 7 characters, starting with # followed by 6 hex digits.'
                : undefined
            }
          />
        </Grid>

        <Grid size={4}>
          <TextField
            required
            fullWidth
            name="heading"
            label="Headline"
            inputProps={{ maxLength: 256 }}
            helperText="The maximum length is 256 characters."
            onChange={handleChangeField}
            value={formValues.heading}
          />
        </Grid>

        <Grid size={8}>
          <TextField
            fullWidth
            name="subheading"
            label="Sub-headline"
            inputProps={{ maxLength: 256 }}
            helperText="The maximum length is 256 characters."
            onChange={handleChangeField}
            value={formValues.subheading || ''}
          />
        </Grid>

        <Grid container spacing={2} marginTop={2}>
          <Grid size={3} alignSelf="center">
            <Typography>Schedule [AEST]</Typography>
          </Grid>
          <Grid size={4}>
            <DateTimeWidget
              required
              defaultTimezone="Australia/Sydney"
              value={formValues.startAt}
              onChange={(date) =>
                setFormValues((prev) => ({
                  ...prev,
                  startAt: date.length ? formatDateDateTimeWidget(new Date(date)) : '',
                }))
              }
              format="DD/MM/YYYY HH:mm"
              label="Start Date"
            />
          </Grid>
          <Grid size={4}>
            <DateTimeWidget
              required
              defaultTimezone="Australia/Sydney"
              value={formValues.endAt}
              onChange={(date) =>
                setFormValues((prev) => ({
                  ...prev,
                  endAt: date.length ? formatDateDateTimeWidget(new Date(date)) : '',
                }))
              }
              format="DD/MM/YYYY HH:mm"
              minDate={formValues.startAt ? newDate(formValues.startAt) : undefined}
              label="End Date"
            />
          </Grid>
          <Grid size={3} alignSelf="center">
            <Typography sx={{ color: dealStatus?.color }}>{dealStatus?.status}</Typography>
          </Grid>
        </Grid>

        <Grid size={12}>
          <ImageUploadField
            col={12}
            label="Hero image"
            field_key="heroImageKey"
            value={formValues.heroImageKey}
            multiple={false}
            onUpload={handleAddImage}
            imageFullWidth
            imageFitType="contain"
          />
        </Grid>

        <Grid size={12}>
          {selectedTours.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Selected tours ({selectedTours.length})</Typography>
              <Box
                sx={{
                  mt: 1,
                  maxHeight: 200,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {selectedTours.map((t) => (
                    <Chip
                      key={t.id}
                      size="small"
                      label={`${t.name} (${t.id})`}
                      onDelete={() => {
                        const newSelected = selectedTours.filter((st) => st.id !== t.id);
                        setSelectedTours(newSelected);
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Box>
          )}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <TextField
              label="Search name"
              value={tourName}
              onChange={(e) => setTourName(e.target.value)}
              size="small"
              sx={{ minWidth: 360 }}
            />
            <TourOfferCountryInput value={countries} onChange={setCountries} />
            <TourOfferTTCBrandInput value={vendors} onChange={setVendors} />
            <TourOfferTourStyleInput value={tourStyles} onChange={setTourStyles} />
          </Stack>
          <DataGrid
            rows={getFilteredRows(rows, tourStyles)}
            columns={columns}
            checkboxSelection
            disableRowSelectionOnClick
            loading={isTourLoading}
            paginationMode="server"
            paginationModel={{ page: pageIndex, pageSize: 20 }}
            rowCount={total}
            onPaginationModelChange={({ page }) => setPageIndex(page)}
            autoHeight
            onRowSelectionModelChange={(selection) => {
              const selectedIds = new Set(selection as Array<string>);
              const currentPage = getFilteredRows(rows, tourStyles).map((r) => ({
                id: normalizeId(r.id),
                name: r.name,
              }));
              // remove deselected from page
              const remaining = selectedTours.filter(
                (t) => !currentPage.find((r) => r.id === t.id) || selectedIds.has(t.id),
              );
              // add newly selected from page
              currentPage.forEach((r) => {
                if (selectedIds.has(r.id) && !remaining.find((t) => t.id === r.id)) {
                  remaining.push({ id: r.id, name: r.name });
                }
              });
              setSelectedTours(remaining);
            }}
            rowSelectionModel={selectedTours.map((t) => t.id)}
            getRowId={(row) => normalizeId(row.id)}
          />
        </Grid>
      </Grid>

      {children}

      <Box mt={3} gap={1} display="flex" justifyContent="end">
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" disabled={!!isFetching || !isFormValid}>
          {!!isFetching && <Spinner size={20} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}
